'use client';

import React, { useMemo, useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import {
  Typography,
  CustomHeroButton,
  AlertBlock,
  ResponsiveModal,
} from '../../../../../components';
import { InfoIcon } from '../../../../../components/icons';
import clientAccService from '../../../../../api/client/clientAccService';
import dollarIconUrl from './dollar-icon.svg';
import infoIconUrl from './info-icon.svg';
import closeIconUrl from './close-icon.svg';
import { useTranslations } from '../../../../../contexts/translation.context';
import { showError, showPartnerSuccess } from '../../../../../utils/toast';
import * as Yup from 'yup';

interface GuaranteeTabProps {
  modelId?: string;
  initialData?: {
    modelName?: string;
    granted?: {
      need?: number;
      dep?: number | null;
      updated_at?: string | null;
      transactions?: any;
    };
  };
  onUpdate?: () => void;
}

const GuaranteeTab: React.FC<GuaranteeTabProps> = ({ modelId, initialData = {}, onUpdate }) => {
  const { t } = useTranslations();
  const [depositAmount, setDepositAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const modelName = initialData.modelName || '';
  const granted = initialData.granted || {};
  const need = granted.need ?? 200;
  const dep = granted.dep ?? null;
  const hasGuarantee = dep !== null && dep >= need;
  const minAmount = need || 200;

  const currentGuaranteeText = useMemo(() => {
    if (hasGuarantee) return `$${dep} из $${need}`;
    return `$0 из $${need}`;
  }, [hasGuarantee, dep, need]);

  // Функция для обработки текста с переносами строк для HTML
  const formatTextForHTML = useCallback((text: string) => {
    if (!text) return '';
    // Обрабатываем различные варианты экранирования и преобразуем в HTML
    let normalizedText = String(text);
    // Заменяем буквальные \n\n на <br/><br/> для HTML
    normalizedText = normalizedText
      .replace(/\\n\\n/g, '<br/><br/>') // Буквальные \n\n -> HTML переносы
      .replace(/\\n/g, '<br/>') // Буквальные \n -> HTML переносы
      .replace(/\n\n+/g, '<br/><br/>') // Реальные переносы -> HTML переносы
      .replace(/\n/g, '<br/>'); // Одиночные переносы -> HTML переносы
    return normalizedText;
  }, []);

  // Подготовка данных для AlertBlock
  const alertData = useMemo(() => {
    const infoDescription = formatTextForHTML(t('accountPage.guarantee.info.description'));
    const terminationDescription = formatTextForHTML(
      t('accountPage.guarantee.termination.description')
    );

    return [
      {
        severity: 'info' as const,
        title: t('accountPage.guarantee.info.title'),
        description: infoDescription,
      },
      {
        severity: 'info' as const,
        title: t('accountPage.guarantee.termination.title'),
        description: terminationDescription,
      },
    ];
  }, [t, formatTextForHTML]);

  // Yup validation schema
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      amount: Yup.number()
        .typeError(t('accountPage.guarantee.validation.mustBeNumber'))
        .min(minAmount, t('accountPage.guarantee.validation.minAmount', { min: minAmount }))
        .required(t('accountPage.guarantee.validation.required')),
    });
  }, [minAmount, t]);

  const validateAmount = useCallback(
    async (value: string): Promise<string | null> => {
      try {
        await validationSchema.validate({ amount: value ? parseFloat(value) : undefined });
        return null;
      } catch (error: any) {
        return error.message || t('accountPage.guarantee.validation.invalid');
      }
    },
    [validationSchema, t]
  );

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDepositAmount(value);
    // Clear error on change
    if (fieldError) setFieldError(null);
  };

  const handleDeposit = async () => {
    if (!modelId) {
      console.error('ModelId не указан');
      return;
    }

    // Validate before sending
    const error = await validateAmount(depositAmount);
    if (error) {
      setFieldError(error);
      return;
    }

    const parsedAmount = parseFloat(depositAmount);

    setIsProcessing(true);
    setFieldError(null);
    try {
      const response = await clientAccService.depositGuarantee(modelId, parsedAmount);
      showPartnerSuccess(t('accountPage.guarantee.messages.depositSuccess'));
      console.log('Гарант успешно пополнен:', response);

      // Очищаем поле после успешного пополнения
      setDepositAmount('');

      // Обновляем данные аккаунта
      onUpdate?.();
    } catch (error: any) {
      console.error('Ошибка при пополнении гаранта:', error);
      const detail = error?.response?.data?.detail;

      // Parse API error
      let message: string;
      if (Array.isArray(detail)) {
        // Handle validation errors from API
        message = detail
          .map((d: any) => {
            if (d?.type === 'greater_than_equal' && d?.ctx?.ge) {
              return t('accountPage.guarantee.validation.minAmount', { min: d.ctx.ge });
            }
            if (d?.msg) {
              // Translate common API error messages
              if (d.msg.includes('greater than or equal')) {
                const match = d.msg.match(/(\d+)/);
                const min = match ? match[1] : minAmount;
                return t('accountPage.guarantee.validation.minAmount', { min });
              }
              return d.msg;
            }
            return JSON.stringify(d);
          })
          .join(', ');
      } else if (typeof detail === 'string') {
        message = detail;
      } else {
        message = t('accountPage.guarantee.messages.depositError');
      }

      // Show error under the input field
      setFieldError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRefundRequest = async () => {
    if (!modelId) {
      console.error('ModelId не найден');
      return;
    }

    try {
      const response = await clientAccService.requestGuaranteeRefund(modelId);
      console.log('Запрос возврата гаранта отправлен:', response);
      showPartnerSuccess(t('accountPage.guarantee.messages.refundSuccess') || 'Запрос отправлен');
      onUpdate?.();
    } catch (error: any) {
      console.error('Ошибка при запросе возврата:', error);
      const detail = error?.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : detail ||
          t('accountPage.guarantee.messages.refundError') ||
          'Ошибка при запросе возврата';
      showError(message);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Current Guarantee Status */}
        <Card
          className={`border border-l-[0.1875rem] border-l-success rounded-none shadow-sm bg-gray-98`}
        >
          <CardBody className="p-4">
            <div className="flex gap-4">
              <div className="min-w-[2.4375rem]">
                <img src={dollarIconUrl} className="size-10" alt="Dollar" />
              </div>
              <div className="flex flex-col gap-0.5">
                <Typography variant="body2" className="!text-successDark font-medium leading-5">
                  {t('accountPage.guarantee.currentGuarantee')}
                </Typography>
                <Typography variant="body2" className="text-auth-muted leading-5">
                  {currentGuaranteeText}
                </Typography>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Guarantee Form */}
        <div className="w-full max-w-[68.5625rem]">
          <Typography variant="h6" className="mb-4">
            {t('accountPage.guarantee.guaranteeFor', { modelName })}
          </Typography>

          <Card className="border border-gray-90 rounded-3.5 shadow-sm">
            <CardBody className="p-4">
              <div className="flex flex-col gap-4">
                {/* Deposit Amount Input */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Typography variant="body2" className="text-auth-ink">
                      {t('accountPage.guarantee.amount')}
                    </Typography>
                  </div>

                  <Input
                    value={depositAmount}
                    onChange={handleAmountChange}
                    placeholder={t('accountPage.guarantee.amountPlaceholder')}
                    variant="bordered"
                    className="w-full h-[2.375rem]"
                    classNames={{
                      inputWrapper: `border-auth-strength-none rounded-lg h-[2.375rem] max-h-[2.375rem] min-h-[2.375rem] ${fieldError ? '!border-auth-strength-weak' : ''}`,
                      input: 'text-auth-muted',
                    }}
                    isInvalid={!!fieldError}
                  />
                  {fieldError && (
                    <Typography variant="body2" className="text-auth-strength-weak text-sm mt-1">
                      {fieldError}
                    </Typography>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
          {/* Action Buttons */}
          <div className="flex gap-4 mt-6">
            {!hasGuarantee && (
              <CustomHeroButton
                variant="primary"
                className="bg-partner text-white rounded-xl h-10 px-6"
                onPress={handleDeposit}
                isLoading={isProcessing}
                disabled={isProcessing || !depositAmount}
                title={
                  isProcessing
                    ? t('accountPage.guarantee.buttons.topUpLoading')
                    : t('accountPage.guarantee.buttons.topUp')
                }
              />
            )}
            {hasGuarantee && (
              <CustomHeroButton
                variant="secondary"
                title={t('accountPage.guarantee.buttons.requestRefund')}
                onPress={() => setIsRefundModalOpen(true)}
              />
            )}
          </div>
        </div>
        <AlertBlock data={alertData} />
      </div>

      <ResponsiveModal
        isOpen={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        hideCloseButton
        size="lg"
        classNames={{
          base: 'bg-white rounded-4 shadow-sm max-w-[23.875rem]',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col items-start pt-6 pb-4 px-4 md:px-6 gap-0">
                <Typography
                  variant="h6"
                  className="text-auth-ink font-medium text-xl leading-normal"
                >
                  {t('accountPage.guarantee.modal.refundTitle')}
                </Typography>
              </ModalHeader>
              <ModalBody className="flex flex-col items-start px-4 md:px-6 pb-3 pt-0">
                <div className="w-full">
                  <Typography
                    variant="body2"
                    className="text-auth-ink text-sm leading-[1.125rem] mb-4"
                  >
                    {t('accountPage.guarantee.modal.refundTextPart1')}{' '}
                    <a
                      href="https://t.me/onlyu_support"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                    >
                      @onlyu_support
                    </a>{' '}
                    {t('accountPage.guarantee.modal.refundTextPart2')}
                  </Typography>
                  <Typography variant="body2" className="text-auth-ink text-sm leading-[1.125rem]">
                    {t('accountPage.guarantee.modal.refundTextDetails')}
                  </Typography>
                </div>
              </ModalBody>
              <ModalFooter className="flex gap-2 justify-center p-4">
                <div
                  className="flex items-center justify-center px-4 h-10 rounded-3 bg-[#f3126033] cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={onClose}
                >
                  <span className="text-auth-strength-weak text-sm font-normal leading-5">
                    {t('accountPage.guarantee.modal.close')}
                  </span>
                </div>
                <a
                  href="https://t.me/onlyu_support"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1 px-3.5 h-10 rounded-3 bg-primary cursor-pointer hover:opacity-90 transition-opacity no-underline"
                  onClick={onClose}
                >
                  <span className="text-white text-sm font-semibold leading-5">
                    {t('accountPage.guarantee.modal.contactManager')}
                  </span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0"
                  >
                    <path
                      d="M5.83331 14.1666L14.1666 5.83331M14.1666 5.83331H5.83331M14.1666 5.83331V14.1666"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </ResponsiveModal>
    </>
  );
};

export default GuaranteeTab;
